<template>
  <div class="tw:relative tw:bg-white tw:rounded-lg tw:border tw:border-gray-300 tw:flex tw:flex-col tw:lg:flex-row">
    <div
      class="tw:flex tw:flex-row tw:items-start tw:md:items-center tw:justify-between tw:w-full tw:lg:w-[300px] tw:p-4 tw:pr-6"
    >
      <div class="tw:flex tw:items-start tw:md:items-center tw:flex-col tw:md:flex-row tw:w-full tw:gap-y-4">
        <div
          class="tw:w-auto tw:md:w-full tw:flex tw:flex-col tw:md:flex-row tw:gap-y-2 tw:justify-between tw:items-start tw:md:items-center tw:flex-wrap"
        >
          <div class="tw:flex tw:flex-col tw:items-start tw:pr-2 tw:lg:flex-[initial] tw:md:flex-[0_0_49%]">
            <h4 class="tw:text-sm tw:font-bold tw:text-gray-900 tw:lg:w-auto" data-testid="product-item-plus-title">
              {{
                product.productType === 'PRO'
                  ? $t('product.proPackage.name')
                  : $t('navigation_menu_business_studio_plus')
              }}
            </h4>
            <div class="tw:flex tw:gap-x-1">
              <p class="tw:text-sm" data-testid="product-item-plus-type">
                {{ $t('business_studio_plus_subscription_header') }}
              </p>
            </div>
          </div>
          <div class="tw:flex tw:items-center tw:gap-x-1 tw:lg:flex-[initial] tw:md:flex-[0_0_49%]">
            <UiTooltip
              v-if="hasTooltip"
              theme="material"
              tooltip-position="top"
              :label="$t(hasTooltip, { EndDate: endDate })"
              class="tw:md:order-0 tw:order-1"
              data-testid="product-item-plus-status-tooltip"
            >
              <InformationOutline class="tw:fill-none tw:stroke-gray-500 tw:h-[16px] tw:w-[16px] tw:ml-1" />
            </UiTooltip>
            <span
              :class="getStatusClasses"
              class="tw:text-sm tw:font-bold tw:py-1 tw:px-2 tw:rounded tw:order-0 tw:md:order-1 tw:text-nowrap"
              data-testid="product-item-plus-status-badge"
              >{{ $t(status[product.status]) }}</span
            >
          </div>
        </div>
        <MainButton
          v-if="product.status === 'CANCELLED' && product.productType !== 'PRO'"
          size="medium"
          class="tw:mr-8 tw:flex tw:md:hidden"
          @click="startReactivation(product.endDate, product.itemId)"
          dataTestId="product-item-plus-reactivate-button-mobile"
          >{{ $t('business_studio_plus_reactivate_cta') }}</MainButton
        >
        <MainButton
          v-if="product.status === 'CANCELLED' && product.productType === 'PRO'"
          size="medium"
          class="tw:mr-8 tw:flex tw:md:hidden"
          @click="startProToPlusReactivation(product.itemId)"
          dataTestId="product-item-plus-pro-to-plus-button-mobile"
          >{{ $t('business_studio_plus_reactivate_cta') }}</MainButton
        >
        <MainButton
          v-if="product.status === 'EXPIRED'"
          size="medium"
          class="tw:mr-8 tw:flex tw:md:hidden"
          @click="startNewSubscription"
          dataTestId="product-item-plus-new-subscription-button-mobile"
          >{{ $t('studio_plus_mp_cta') }}</MainButton
        >
      </div>
      <div class="tw:flex tw:lg:hidden tw:justify-end tw:w-full tw:items-center tw:mt-4 tw:md:mt-0">
        <MainButton
          v-if="product.status === 'CANCELLED' && product.productType !== 'PRO'"
          size="medium"
          class="tw:mr-8 tw:hidden tw:md:flex tw:lg:hidden"
          @click="startReactivation(product.endDate, product.itemId)"
          dataTestId="product-item-plus-reactivate-button-tablet"
          >{{ $t('business_studio_plus_reactivate_cta') }}</MainButton
        >
        <MainButton
          v-if="product.status === 'CANCELLED' && product.productType === 'PRO'"
          size="medium"
          class="tw:mr-8 tw:hidden tw:md:flex tw:lg:hidden"
          @click="startProToPlusReactivation(product.itemId)"
          dataTestId="product-item-plus-pro-to-plus-button-tablet"
          >{{ $t('business_studio_plus_reactivate_cta') }}</MainButton
        >
        <MainButton
          v-if="product.status === 'EXPIRED'"
          size="medium"
          class="tw:mr-8 tw:hidden tw:md:flex tw:lg:hidden"
          @click="startNewSubscription"
          dataTestId="product-item-plus-new-subscription-button-tablet"
          >{{ $t('studio_plus_mp_cta') }}</MainButton
        >
        <div class="tw:relative tw:flex tw:justify-end tw:min-w-[30px]" v-click-outside="closeMobileMenu">
          <button
            class="tw:hover:bg-gray-200 tw:rounded-md"
            @click="toggleMobileMenu"
            :class="{
              'tw:bg-gray-200 tw:rounded-md': mobileMenuVisible !== null && mobileMenuVisible === product.itemId,
            }"
            data-testid="product-item-plus-options-menu-mobile"
          >
            <img :src="dots" />
          </button>
          <div
            v-show="mobileMenuVisible !== null && mobileMenuVisible === product.itemId"
            @click.stop
            class="tw:absolute tw:right-0 tw:top-[100%] tw:w-[300px] tw:w-[min(300px,calc(100vw-100px))] tw:bg-white shadow tw:mt-1 tw:rounded-lg tw:z-30 tw:max-h-[300px] tw:overflow-auto"
          >
            <div
              :class="product.status === 'ACTIVE' ? 'tw:rounded-t-lg' : 'tw:rounded-lg'"
              class="tw:px-4 tw:py-3 tw:text-sm tw:hover:bg-blue-200 tw:hover:text-blue-800"
              @click="openInvoices"
              data-testid="product-item-plus-review-invoices-mobile"
            >
              {{ $t('tbs_invoice_review') }}
            </div>
            <div
              v-if="product.status === 'ACTIVE'"
              class="tw:px-4 tw:py-3 tw:text-sm tw:hover:bg-blue-200 tw:hover:text-blue-800 tw:rounded-b-lg"
              @click="
                openCancellation({
                  accommodationName,
                  accommodationId: product.itemId,
                  productType: product.productType,
                })
              "
              data-testid="product-item-plus-cancel-subscription-mobile"
            >
              {{ $t('subscriptions.details.cancel.cta') }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="tw:bg-gray-100 tw:py-4 tw:px-4 tw:lg:px-8 tw:flex tw:w-full tw:lg:w-[calc(100%-300px)] tw:rounded-b-lg tw:lg:rounded-r-lg"
    >
      <div class="tw:flex tw:flex-wrap tw:w-full tw:gap-4 tw:flex-[0_0_100%] tw:lg:flex-[0_0_70%]">
        <div class="tw:flex-[1_1_45%] tw:md:flex-[0_0_23%] tw:lg:flex-[0_0_30%] tw:flex tw:flex-col tw:gap-y-2">
          <p class="tw:text-sm tw:font-bold">{{ $t('subscription_billing') }}</p>
          <p class="tw:text-sm" data-testid="product-item-plus-billing-frequency">
            {{ product.term === 'MONTHLY' ? $t('subscription_billing_monthly') : $t('subscription_billing_annually') }}
          </p>
        </div>
        <div class="tw:flex-[1_1_45%] tw:md:flex-[0_0_23%] tw:lg:flex-[0_0_30%] tw:flex tw:flex-col tw:gap-y-2">
          <p class="tw:text-sm tw:font-bold">{{ $t('subscription_start') }}</p>
          <p class="tw:text-sm" data-testid="product-item-plus-start-date">{{ startDate }}</p>
        </div>
        <div class="tw:flex-[1_1_45%] tw:md:flex-[0_0_23%] tw:lg:flex-[0_0_30%]">
          <div v-if="product.status === 'ACTIVE'" class="tw:flex tw:flex-col tw:gap-y-2">
            <p class="tw:text-sm tw:font-bold">
              {{ $t('plus_renewal_date') }}
            </p>
            <p class="tw:text-sm" data-testid="product-item-plus-next-payment">
              {{ nextPaymentDate }} {{ '|' }} {{ nextPaymentAmount }}
            </p>
          </div>
          <div v-if="product.status === 'CANCELLED'" class="tw:flex tw:flex-col tw:gap-y-2">
            <p class="tw:text-sm tw:font-bold">
              {{ $t('active_subscription') }}
            </p>
            <p class="tw:text-sm" data-testid="product-item-plus-active-until">{{ endDate }}</p>
          </div>
          <div v-if="product.status === 'EXPIRED'" class="tw:flex tw:flex-col tw:gap-y-2">
            <p class="tw:text-sm tw:font-bold">
              {{ $t('subscription_expiry_date') }}
            </p>
            <p class="tw:text-sm" data-testid="product-item-plus-expiry-date">{{ endDate }}</p>
          </div>
        </div>
      </div>

      <div class="tw:hidden tw:lg:flex tw:justify-end tw:w-full tw:items-center tw:gap-x-4">
        <MainButton
          v-if="product.status === 'CANCELLED' && product.productType !== 'PRO'"
          size="medium"
          @click="startReactivation(product.endDate, product.itemId)"
          dataTestId="product-item-plus-reactivate-button-desktop"
          >{{ $t('business_studio_plus_reactivate_cta') }}</MainButton
        >
        <MainButton
          v-if="product.status === 'CANCELLED' && product.productType === 'PRO'"
          size="medium"
          @click="startProToPlusReactivation(product.itemId)"
          dataTestId="product-item-plus-pro-to-plus-button-desktop"
          >{{ $t('business_studio_plus_reactivate_cta') }}</MainButton
        >
        <MainButton
          v-if="product.status === 'EXPIRED'"
          size="medium"
          @click="startNewSubscription"
          dataTestId="product-item-plus-new-subscription-button-desktop"
          >{{ $t('studio_plus_mp_cta') }}</MainButton
        >
        <div class="tw:flex tw:justify-end" v-click-outside="closeMenu">
          <button
            class="tw:hover:bg-gray-200 tw:rounded-md tw:w-[36px] tw:h-[36px]"
            @click="toggleMenu"
            :class="{ 'tw:bg-gray-200 tw:rounded-md': menuVisible !== null && menuVisible === product.itemId }"
            data-testid="product-item-plus-options-menu-desktop"
          >
            <img :src="dots" />
          </button>
          <div
            v-show="menuVisible !== null && menuVisible === product.itemId"
            @click.stop
            class="tw:absolute tw:right-0 tw:top-[100%] tw:w-[300px] tw:w-[min(300px,calc(100vw-100px))] tw:bg-white shadow tw:mt-1 tw:rounded-lg tw:z-30 tw:max-h-[300px] tw:overflow-auto"
          >
            <div
              :class="product.status === 'ACTIVE' ? 'tw:rounded-t-lg' : 'tw:rounded-lg'"
              class="tw:px-4 tw:py-3 tw:text-sm tw:hover:bg-blue-200 tw:hover:text-blue-800"
              @click="openInvoices"
              data-testid="product-item-plus-review-invoices-desktop"
            >
              {{ $t('tbs_invoice_review') }}
            </div>
            <div
              v-if="product.status === 'ACTIVE'"
              class="tw:px-4 tw:py-3 tw:text-sm tw:hover:bg-blue-200 tw:hover:text-blue-800 tw:rounded-b-lg"
              @click="
                openCancellation({
                  accommodationName,
                  accommodationId: product.itemId,
                  productType: product.productType,
                })
              "
              data-testid="product-item-plus-cancel-subscription-desktop"
            >
              {{ $t('subscriptions.details.cancel.cta') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import UiTooltip from '@/components/dashboard/ui/UiTooltip.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import InformationOutline from '@/components/icons/InformationOutline.svg?component'

import { useStudioPlusReactivation } from '@/components/billing/premiumProducts/reactivation/useStudioPlusReactivation'
import { useProToPlusReactivation } from '@/components/billing/premiumProducts/reactivation/useProToPlusReactivation'
import { useInvoicesFilters } from '@/components/billing/invoices/useInvoicesFilters'
import { setSelectedAccommodationId } from '@/layouts/queries'
import { useFormatting } from '@/composables/useFormatting'

import appAnalytics from '@/utils/tracking'

const dots = new URL(`../../../assets/img/rate-connect/dropdown-dots.svg`, import.meta.url)

const { openReactivationModal, setIsCancelled, setIsExpired, openCancellationModal } = useStudioPlusReactivation()
const { openProToPlusModal } = useProToPlusReactivation()

const { formatCurrency, formatDate } = useFormatting()
const status = {
  ACTIVE: 'subscriptions.status.active',
  EXPIRED: 'subscriptions.status.expired',
  CANCELLED: 'subscriptions.status.cancelled',
}
const router = useRouter()
const props = defineProps({
  product: Object,
  accommodationName: String,
})
const mobileMenuVisible = ref(null)
const menuVisible = ref(null)
const menuItemType = ref(null)
const {
  selectedPropertyValues,
  selectedProductValues,
  productTypeOptions,
  proProductOption,
  timeframeValue,
  setStartDate,
  setEndDate,
} = useInvoicesFilters()

const getStatusClasses = computed(() => {
  if (props.product.endDate === null) return 'tw:text-green-700 tw:bg-green-200'
  else return 'tw:text-red-700 tw:bg-red-200'
})

const openCancellation = ({ accommodationName, accommodationId, productType }) => {
  appAnalytics.track(appAnalytics.events.BILLING_CANCEL_PLUS_SUBSCRIPTION, {
    item_id: accommodationId,
  })
  openCancellationModal({ accommodationName, accommodationId, productType })

  closeMenu()
  closeMobileMenu()
}

const openMenu = () => {
  menuVisible.value = props.product.itemId
  menuItemType.value = props.product.productType
}

const closeMenu = () => {
  menuVisible.value = null
  menuItemType.value = null
}
const openMobileMenu = () => {
  mobileMenuVisible.value = props.product.itemId
  menuItemType.value = props.product.productType
}

const toggleMobileMenu = () => {
  if (mobileMenuVisible.value) {
    closeMobileMenu()
  } else {
    openMobileMenu()
  }
}

const toggleMenu = () => {
  if (mobileMenuVisible.value) {
    closeMenu()
  } else {
    openMenu()
  }
}
const closeMobileMenu = () => {
  mobileMenuVisible.value = null
  menuItemType.value = null
}

const nextPaymentDate = computed(() => {
  return formatDate(props.product.nextPaymentDate)
})

const startDate = computed(() => {
  return formatDate(props.product?.startDate)
})

const endDate = computed(() => formatDate(props.product?.endDate))

const nextPaymentAmount = computed(() => {
  return formatCurrency(props.product?.nextPaymentAmount, { divideBy100: false })
})

const startProToPlusReactivation = accommodationId => {
  appAnalytics.track(appAnalytics.events.PRO_TO_PLUS_MODAL_OPENED, {
    item_id: accommodationId,
    source: 'billing',
  })
  openProToPlusModal(accommodationId, 'billing')
}

const startReactivation = (endDate, accommodationId) => {
  appAnalytics.track(appAnalytics.events.BILLING_REACTIVATE_PLUS_SUBSCRIPTION, {
    item_id: props.product.itemId,
  })
  setIsExpired(endDate)
  setIsCancelled(endDate)
  openReactivationModal(accommodationId, 'premium-products-page')
}

const startNewSubscription = () => {
  appAnalytics.track(appAnalytics.events.BILLING_START_PLUS_SUBSCRIPTION, {
    item_id: props.product.itemId,
  })
  setSelectedAccommodationId(props.product.itemId)
  router.push({ name: 'studio-plus-compare' })
}

const openInvoices = () => {
  appAnalytics.track(appAnalytics.events.BILLING_REVIEW_INVOICES, {
    item_id: props.product.itemId,
  })
  const currentProduct =
    props.product.productType === 'PRO'
      ? [proProductOption]
      : productTypeOptions.value.filter(item => item.value.toLowerCase() === props.product.productType.toLowerCase())

  selectedPropertyValues.value = [
    { id: props.product.itemId, label: props.accommodationName, value: props.product.itemId },
  ]
  timeframeValue.value = { id: 'last-12-months', label: 'last_12_months', value: '12' }
  setStartDate({ id: 'last-12-months', label: 'last_12_months', value: '12' })
  setEndDate()
  selectedProductValues.value = currentProduct
  router.push({ name: 'billing-invoices' })
}

const hasTooltip = computed(() => {
  if (props.product.productType === 'PLUS') {
    if (props.product.status === 'EXPIRED') return 'plus_expired_tooltip'
    else if (props.product.status === 'CANCELLED') return 'plus_canceled_tooltip'
    else return false
  } else if (props.product.productType === 'PRO') {
    if (props.product.status === 'EXPIRED') return 'pro_expired_tooltip'
    else if (props.product.status === 'CANCELLED') return 'pro_cancled_tooltip'
    else return false
  } else return false
})
</script>
